import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Chart } from "chart.js";
import { createAppTheme } from "../theme/mui-theme";

const STORAGE_KEY = "colorMode";

export function readColorMode() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch (e) {
    return "light";
  }
}

if (typeof document !== "undefined" && readColorMode() === "dark") {
  document.documentElement.dataset.theme = "dark";
}

const ThemeModeContext = createContext({
  mode: "light",
  isDark: false,
  toggleColorMode: () => {},
});

function applyChartDefaults(mode) {
  const dark = mode === "dark";
  Chart.defaults.color = dark ? "#e6e6e6" : "#666666";
  Chart.defaults.borderColor = dark
    ? "rgba(255,255,255,0.14)"
    : "rgba(0,0,0,0.1)";
  if (typeof document === "undefined") return;
  document.querySelectorAll("canvas").forEach((node) => {
    const chart = Chart.getChart(node);
    if (chart) chart.update();
  });
}

export function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(readColorMode);
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      /* storage unavailable */
    }
    applyChartDefaults(mode);
  }, [mode]);

  const toggleColorMode = useCallback(() => {
    setMode((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      mode,
      isDark: mode === "dark",
      toggleColorMode,
    }),
    [mode, toggleColorMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

export function chartInk(mode) {
  return mode === "dark" ? "#9c9489" : "#000000";
}

export function chartTrack(mode) {
  return mode === "dark" ? "#3a3a3d" : "#000000";
}

export function chartLabel(mode) {
  return mode === "dark" ? "#e8e8e8" : "#666666";
}

export function chartGrid(mode) {
  return mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.1)";
}
